/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BehanceData, ImsOrganization} from '../models/IMS';
import {getValueFromPath} from '@exc/url';

const w = window as any;

/**
 * Returns true if the navigation was an org change.
 * @param {string} oldPath window location pathname.
 * @param {string} newPath history location pathname.
 * @returns {boolean} Returns true if the navigation was an org change.
 */
export function isOrgOrSubOrgChange(oldPath: string, newPath: string): boolean {
  const oldPathArr = oldPath.split('/');
  const newPathArr = newPath.split('/');

  // Check if the suborg has changed
  if (getValueFromPath(oldPath, 'so') !== getValueFromPath(newPath, 'so')) {
    return true;
  }
  // If the length is not the same, more has changed than just the tenant meaning
  // it is not an org change.
  if (oldPathArr.length !== newPathArr.length) {
    return false;
  }
  // Because we are splitting by '/' arr[0] will be "" and arr[1] will be the tenant.
  for (let i = 2; i < oldPathArr.length - 1; i++) {
    if (oldPathArr[i] !== newPathArr[i]) {
      return false;
    }
  }
  return oldPathArr[1] !== newPathArr[1];
}

/**
 * Get name of org.
 * @param {string} org String with org.
 * @param {ImsOrganization[]} map List of orgs.
 * @returns {string} Org name or unknown.
 */
export function getOrgName(org: string, map: ImsOrganization[]): string {
  const s = map.find(({imsOrgId}) => imsOrgId === org);
  return s ? s.orgName : 'unknown';
}

type PropertyBag = {
  [key: string]: any;
};

/**
 * Copies a property from object payload to another object reduced payload.
 * @param {object} toObject Destination object
 * @param {object} fromObject Original object.
 * @param {string} property String denoting the property to copy over.
 */
export function copyProperty(toObject: PropertyBag, fromObject: PropertyBag, property: string): void {
  function copy(destinationObject: PropertyBag, originObject: PropertyBag, parts: string[]): void {
    const currentKey = parts[0];
    if (parts.length > 1) {
      destinationObject[currentKey] = destinationObject[currentKey] || {};
      return copy(destinationObject[currentKey], originObject[currentKey], parts.slice(1));
    }
    destinationObject[currentKey] = originObject[currentKey];
  }
  copy(toObject, fromObject, property.split('.'));
}

interface AvatarUrls {
  avatar?: string;
  avatarSrc?: string;
}
/**
 * Gets the preferred avatar URL from the Behance payload.
 * @param {BehanceData} avatar Behance avatar information.
 * @param {number[]} sizes Array of preferred sizes, ordered by preference.
 * @returns {AvatarUrls} Avatar URL and Data URL.
 */
export function getAvatarUrls(avatar: BehanceData, sizes: number[]): AvatarUrls {
  let url: string|undefined;
  if (avatar && avatar.images) {
    sizes.some(size => url = avatar.images[String(size)]);
    return {
      avatar: url,
      avatarSrc: avatar.encodedImage ? `data:image/jpeg;base64, ${avatar.encodedImage}` : undefined
    };
  }
  return {};
}

let gainsightPromise: Promise<() => boolean|undefined>|undefined;

/**
 * Promise for grabbing the gainsight callback off of the window. This will
 * be used to trigger when a new app is loaded. This method only runs the
 * promise on the first run and caches the result for future calls.
 * @param {boolean} ignorePromise Ignores the existing promise. FOR TESTING ONLY.
 * @returns {Promise} The promise that will include the callback.
 */
export const waitForGainsightCallback = (ignorePromise: boolean): Promise<() => boolean|undefined> => {
  if (gainsightPromise && !ignorePromise) {
    return gainsightPromise;
  }
  return gainsightPromise = new Promise(res => {
    let cnt = 0;
    function pollForCallback(): void {
      cnt++;
      if (w.initGainsight || cnt >= 10) {
        res(w.initGainsight || ((): boolean => false));
        return;
      }
      setTimeout(pollForCallback, 500);
    }
    setTimeout(pollForCallback, 500);
  });
};

/**
 * Enhances security and defense against HTML injections on post messages.
 * @returns {string} Valid source strings translated to a regex string.
 */
export function getValidSourcesRegexString(): RegExp {
  const frameSrc = w.config.csp?.frameSrc || [];

  if (!frameSrc.length) {
    return new RegExp('.*');
  }

  let regexSources = '';
  frameSrc.filter((str: string) => !str.includes('blob:')).map((str: string) => {
    str = str.replace(/\./g, '\\.').replace(/\*/g, '.*').replace(/\/\//g, '\\/\\/');
    regexSources += `${str}$|`;
  });

  return new RegExp(regexSources.slice(0, -2));
}

/**
 * Wait for idle time to execute.
 * (On browsers supporting the requestIdleCallback API)
 * @param {boolean} background - Wait for idle? (Defaults to true)
 */
export function runInBackground(background = true): Promise<void> {
  // TODO: Timeout based Polyfill for Safari?
  const requestCallback = w.requestIdleCallback ?
    w.requestIdleCallback :
    (callback: () => Promise<any>): Promise<any> => callback();
  if (background) {
    return new Promise(resolve => requestCallback(resolve));
  }
  return Promise.resolve();
}

/**
 * Executes a request using a stale-while-revalidate strategy.
 * If data already exists, return it right away and fetch in the background.
 * Otherwise, fetch.
 * @param {() => Promise<T>} getPromise A function to request data.
 * @param {T} cached cached data (Or undefined if no data cached)
 * @param {boolean} runImmediate Whether or not to wait until page is idle to execute promise.
 * @param {err => void} onBackgroundError (Optional) Error handler for background requests only.
 */
export function staleWhileRevalidate<T>(
  getPromise: () => Promise<T>,
  cached: T | undefined,
  runImmediate = false,
  onBackgroundError?: (err: Error) => void): Promise<T> {

  if (cached) {
    (runImmediate ? Promise.resolve() : runInBackground())
      .then(getPromise)
      .catch(err => onBackgroundError && onBackgroundError(err));
    return Promise.resolve(cached);
  }
  return getPromise();
}

/**
 * Awaits a promise up to a certain timeout.
 * If the timeout is due before the promise resolves, resolves with a predefined payload.
 * @param {Promise<T>} promise to await.
 * @param {number} timeout - Timeout (ms)
 * @param {T} timeoutResponse - Timeout response
 */
export async function runWithTimeout<T>(promise: Promise<T>, timeout: number, timeoutResponse: T): Promise<T> {
  const timeoutPromise: Promise<T> = new Promise(resolve => setTimeout(() => resolve(timeoutResponse), timeout));
  return await Promise.race([promise, timeoutPromise]);
}
